/**
 * Bulk Upload Validators
 *
 * Reusable validation functions for bulk upload operations
 */

import * as XLSX from 'xlsx';
import type { BulkUploadConfig, ContentValidationResult } from '../types/bulk-upload';

/**
 * UUID pattern for consent ID validation
 */
export const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Check if a string is a valid UUID
 */
export const isValidUUID = (value: string): boolean => {
  return UUID_PATTERN.test(value);
};

/**
 * Check if a string is a valid date
 */
export const isValidDate = (dateStr: string): boolean => {
  if (!dateStr || dateStr.trim() === '') return false;
  const date = new Date(dateStr);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Validate date range (from <= to)
 */
export const validateDateRange = (fromDate: string, toDate: string): boolean => {
  if (!isValidDate(fromDate) || !isValidDate(toDate)) return false;
  return new Date(fromDate) <= new Date(toDate);
};

/**
 * Validate file content based on configuration
 */
export const validateFileContent = (
  file: File,
  config: BulkUploadConfig
): Promise<ContentValidationResult> => {
  return new Promise((resolve) => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const reader = new FileReader();
    const errors: string[] = [];

    reader.onerror = () => {
      errors.push('Failed to read file for content validation');
      resolve({ errors });
    };

    if (fileExtension === '.csv') {
      validateCSVContent(reader, file, config, resolve);
    } else if (fileExtension === '.json') {
      validateJSONContent(reader, file, config, resolve);
    } else {
      validateExcelContent(reader, file, config, resolve);
    }
  });
};

/**
 * Validate CSV file content
 */
const validateCSVContent = (
  reader: FileReader,
  file: File,
  config: BulkUploadConfig,
  resolve: (result: ContentValidationResult) => void
) => {
  reader.onload = (event: any) => {
    try {
      const text = event.target.result as string;
      const lines = text.split(/\r\n|\n|\r/).filter(line => line.trim().length > 0);

      if (lines.length < 2) {
        resolve({ errors: ['File is empty or has no data rows'] });
        return;
      }

      // Parse header
      const header = lines[0].split(',').map(h => h.trim());

      // Find column indices
      const columnIndices = getColumnIndices(header, config);

      // Check required columns exist
      const missingColumns = checkRequiredColumns(columnIndices, config);
      if (missingColumns.length > 0) {
        resolve({ errors: [`Missing required columns: ${missingColumns.join(', ')}`] });
        return;
      }

      // Check for unexpected columns
      const unexpectedColumns = checkUnexpectedColumns(header, config);
      if (unexpectedColumns.length > 0) {
        resolve({
          errors: [
            `Unexpected columns found: ${unexpectedColumns.join(', ')}`,
            'Please use the provided sample template with correct column headers',
          ],
        });
        return;
      }

      const errorTypes = new Set<string>();
      const allRows: Record<string, any>[] = [];
      const duplicateTracker = config.checkDuplicatesInColumn
        ? new Set<string>()
        : null;
      const duplicates = config.checkDuplicatesInColumn
        ? new Set<string>()
        : null;

      // Validate each data row
      for (let i = 1; i < lines.length; i++) {
        const rowNum = i + 1;
        const cols = lines[i].split(',').map(c => c.trim());

        // Build row data object
        const rowData: Record<string, any> = {};
        config.columns.forEach(col => {
          const idx = columnIndices[col.name];
          rowData[col.name] = idx !== undefined ? cols[idx] || '' : '';
        });

        // Skip completely empty rows
        const isEmptyRow = Object.values(rowData).every(val => !val || val.trim() === '');
        if (isEmptyRow) {
          continue;
        }

        allRows.push(rowData);

        // Validate row and collect error types
        const rowErrorTypes = validateRowAndCollectTypes(rowData, rowNum, config);
        rowErrorTypes.forEach(errorType => errorTypes.add(errorType));

        // Track duplicates
        if (config.checkDuplicatesInColumn && duplicateTracker && duplicates) {
          const value = rowData[config.checkDuplicatesInColumn];
          if (value && duplicateTracker.has(value)) {
            duplicates.add(value);
          } else if (value) {
            duplicateTracker.add(value);
          }
        }
      }

      // Apply custom validations
      if (config.customValidations) {
        for (const rule of config.customValidations) {
          for (let i = 0; i < allRows.length; i++) {
            const error = rule.validate(allRows[i], i + 2, allRows);
            if (error) {
              errorTypes.add(error);
            }
          }
        }
      }

      // Convert error types to array
      const errors = Array.from(errorTypes);

      // Build result
      const result: ContentValidationResult = { errors };
      if (duplicates && duplicates.size > 0) {
        result.duplicates = {
          column: config.checkDuplicatesInColumn!,
          values: Array.from(duplicates).slice(0, 3),
          count: duplicates.size,
        };
        errors.push(
          `Found ${duplicates.size} duplicate ${config.checkDuplicatesInColumn}(s): ${result.duplicates.values.join(', ')}${duplicates.size > 3 ? '...' : ''}`
        );
      }

      resolve(result);
    } catch (error) {
      resolve({ errors: ['Failed to parse CSV file for validation'] });
    }
  };
  reader.readAsText(file);
};

/**
 * Validate JSON file content
 */
const validateJSONContent = (
  reader: FileReader,
  file: File,
  config: BulkUploadConfig,
  resolve: (result: ContentValidationResult) => void
) => {
  reader.onload = (event: any) => {
    try {
      const text = event.target.result as string;
      const jsonData = JSON.parse(text);

      if (!Array.isArray(jsonData)) {
        resolve({ errors: ['JSON file must contain an array of records'] });
        return;
      }

      if (jsonData.length === 0) {
        resolve({ errors: ['JSON file is empty'] });
        return;
      }

      // Check for unexpected columns in JSON keys
      const firstRecord = jsonData[0];
      if (firstRecord && typeof firstRecord === 'object') {
        const jsonKeys = Object.keys(firstRecord);
        const unexpectedColumns = checkUnexpectedColumns(jsonKeys, config);
        if (unexpectedColumns.length > 0) {
          resolve({
            errors: [
              `Unexpected columns found: ${unexpectedColumns.join(', ')}`,
              'Please use the provided sample template with correct column headers',
            ],
          });
          return;
        }
      }

      const errorTypes = new Set<string>();
      const validRows: any[] = [];
      const duplicateTracker = config.checkDuplicatesInColumn
        ? new Set<string>()
        : null;
      const duplicates = config.checkDuplicatesInColumn
        ? new Set<string>()
        : null;

      jsonData.forEach((record: any, index: number) => {
        const rowNum = index + 1;

        // Skip completely empty records
        const isEmptyRecord = Object.values(record).every(
          val => val === null || val === undefined || val === ''
        );
        if (isEmptyRecord) {
          return;
        }

        validRows.push(record);

        // Validate row and collect error types
        const rowErrorTypes = validateRowAndCollectTypes(record, rowNum, config);
        rowErrorTypes.forEach(errorType => errorTypes.add(errorType));

        // Track duplicates
        if (config.checkDuplicatesInColumn && duplicateTracker && duplicates) {
          const value = record[config.checkDuplicatesInColumn];
          if (value && duplicateTracker.has(value)) {
            duplicates.add(value);
          } else if (value) {
            duplicateTracker.add(value);
          }
        }
      });

      // Apply custom validations
      if (config.customValidations) {
        for (const rule of config.customValidations) {
          for (let i = 0; i < validRows.length; i++) {
            const error = rule.validate(validRows[i], i + 1, validRows);
            if (error) {
              errorTypes.add(error);
            }
          }
        }
      }

      // Convert error types to array
      const errors = Array.from(errorTypes);

      // Build result
      const result: ContentValidationResult = { errors };
      if (duplicates && duplicates.size > 0) {
        result.duplicates = {
          column: config.checkDuplicatesInColumn!,
          values: Array.from(duplicates).slice(0, 3),
          count: duplicates.size,
        };
        errors.push(`Found ${duplicates.size} duplicate ${config.checkDuplicatesInColumn}(s)`);
      }

      resolve(result);
    } catch (error) {
      resolve({ errors: ['Failed to parse JSON file for validation'] });
    }
  };
  reader.readAsText(file);
};

/**
 * Validate Excel file content
 */
const validateExcelContent = (
  reader: FileReader,
  file: File,
  config: BulkUploadConfig,
  resolve: (result: ContentValidationResult) => void
) => {
  reader.onload = (event: any) => {
    try {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array', bookVBA: true });

      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        resolve({ errors: ['Excel file is empty or corrupted'] });
        return;
      }

      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: any[][] = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        defval: '',
      });

      if (jsonData.length < 2) {
        resolve({ errors: ['File has no data rows'] });
        return;
      }

      // Parse header
      const header = jsonData[0].map((h: any) => String(h).trim());

      // Find column indices
      const columnIndices = getColumnIndices(header, config);

      // Check required columns
      const missingColumns = checkRequiredColumns(columnIndices, config);
      if (missingColumns.length > 0) {
        resolve({ errors: [`Missing required columns: ${missingColumns.join(', ')}`] });
        return;
      }

      // Check for unexpected columns
      const unexpectedColumns = checkUnexpectedColumns(header, config);
      if (unexpectedColumns.length > 0) {
        resolve({
          errors: [
            `Unexpected columns found: ${unexpectedColumns.join(', ')}`,
            'Please use the provided sample template with correct column headers',
          ],
        });
        return;
      }

      const errorTypes = new Set<string>();
      const allRows: Record<string, any>[] = [];
      const duplicateTracker = config.checkDuplicatesInColumn
        ? new Set<string>()
        : null;
      const duplicates = config.checkDuplicatesInColumn
        ? new Set<string>()
        : null;

      for (let i = 1; i < jsonData.length; i++) {
        const rowNum = i + 1;
        const row = jsonData[i];

        // Build row data object
        const rowData: Record<string, any> = {};
        config.columns.forEach(col => {
          const idx = columnIndices[col.name];
          rowData[col.name] = idx !== undefined ? String(row[idx] || '').trim() : '';
        });

        // Skip completely empty rows
        const isEmptyRow = Object.values(rowData).every(val => !val || val.trim() === '');
        if (isEmptyRow) {
          continue;
        }

        allRows.push(rowData);

        // Validate row and collect error types
        const rowErrorTypes = validateRowAndCollectTypes(rowData, rowNum, config);
        rowErrorTypes.forEach(errorType => errorTypes.add(errorType));

        // Track duplicates
        if (config.checkDuplicatesInColumn && duplicateTracker && duplicates) {
          const value = rowData[config.checkDuplicatesInColumn];
          if (value && duplicateTracker.has(value)) {
            duplicates.add(value);
          } else if (value) {
            duplicateTracker.add(value);
          }
        }
      }

      // Apply custom validations
      if (config.customValidations) {
        for (const rule of config.customValidations) {
          for (let i = 0; i < allRows.length; i++) {
            const error = rule.validate(allRows[i], i + 2, allRows);
            if (error) {
              errorTypes.add(error);
            }
          }
        }
      }

      // Convert error types to array
      const errors = Array.from(errorTypes);

      // Build result
      const result: ContentValidationResult = { errors };
      if (duplicates && duplicates.size > 0) {
        result.duplicates = {
          column: config.checkDuplicatesInColumn!,
          values: Array.from(duplicates).slice(0, 3),
          count: duplicates.size,
        };
        errors.push(`Found ${duplicates.size} duplicate ${config.checkDuplicatesInColumn}(s)`);
      }

      resolve(result);
    } catch (error) {
      resolve({ errors: ['Failed to parse Excel file for validation'] });
    }
  };
  reader.readAsArrayBuffer(file);
};

/**
 * Get column indices from header
 */
const getColumnIndices = (
  header: string[],
  config: BulkUploadConfig
): Record<string, number> => {
  const indices: Record<string, number> = {};

  config.columns.forEach(col => {
    const idx = header.findIndex(h => h.toLowerCase() === col.name.toLowerCase());
    if (idx !== -1) {
      indices[col.name] = idx;
    }
  });

  return indices;
};

/**
 * Check if all required columns exist
 */
const checkRequiredColumns = (
  columnIndices: Record<string, number>,
  config: BulkUploadConfig
): string[] => {
  const missing: string[] = [];

  config.columns.forEach(col => {
    if (col.required && columnIndices[col.name] === undefined) {
      missing.push(col.displayName || col.name);
    }
  });

  return missing;
};

/**
 * Check for unexpected columns not in config
 */
const checkUnexpectedColumns = (
  header: string[],
  config: BulkUploadConfig
): string[] => {
  // When allowExtraColumns is true, extra columns pass through as csv_meta
  if (config.allowExtraColumns) return [];

  const unexpected: string[] = [];

  header.forEach(headerCol => {
    const trimmedHeader = headerCol.trim();
    // Skip empty headers
    if (!trimmedHeader) return;

    // Check if this header exists in config columns (case-insensitive)
    const found = config.columns.some(
      col => col.name.toLowerCase() === trimmedHeader.toLowerCase()
    );

    if (!found) {
      unexpected.push(trimmedHeader);
    }
  });

  return unexpected;
};

/**
 * Validate a single row and collect error types (not row-specific errors)
 */
const validateRowAndCollectTypes = (
  rowData: Record<string, any>,
  rowNum: number,
  config: BulkUploadConfig
): string[] => {
  const errorTypes: string[] = [];

  config.columns.forEach(col => {
    const value = rowData[col.name];
    const displayName = col.displayName || col.name;

    // Check required
    if (col.required && (!value || value.trim() === '')) {
      errorTypes.push(`Please enter valid ${displayName}`);
      return;
    }

    // Skip further validation if empty and not required
    if (!value || value.trim() === '') {
      return;
    }

    // Type validation
    switch (col.type) {
      case 'uuid':
        if (!isValidUUID(value)) {
          errorTypes.push(`Please enter valid ${displayName} format (UUID)`);
        }
        break;

      case 'date':
        if (!isValidDate(value)) {
          errorTypes.push(`Please enter valid date format for ${displayName} (YYYY-MM-DD)`);
        }
        break;

      case 'number':
        if (isNaN(Number(value))) {
          errorTypes.push(`${displayName} must be a number`);
        }
        break;

      case 'boolean':
        const normalized = value.toLowerCase();
        if (!['true', 'false', '1', '0', 'yes', 'no'].includes(normalized)) {
          errorTypes.push(`${displayName} must be true/false or yes/no`);
        }
        break;
    }

    // Custom validator (keep as-is for specific messages)
    if (col.validator) {
      const error = col.validator(value, rowNum, rowData);
      if (error) {
        errorTypes.push(error);
      }
    }
  });

  return errorTypes;
};

